/**
 * API utility functions for making HTTP requests
 */

import axios from 'axios';
import { environment } from '../config/environment';

const API_BASE_URL = environment.BASE_URL;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let msalInstance = null;
const publicUrl = process.env.PUBLIC_URL || '';
const loginPath = `${publicUrl}/login`;

// Enhanced request throttling - track requests in progress with timestamps
const pendingRequests = {};

// Cleanup old pending requests periodically (every 30 seconds)
setInterval(() => {
  const now = Date.now();
  const CLEANUP_THRESHOLD = 30000; // 30 seconds
  
  Object.keys(pendingRequests).forEach(key => {
    if (now - pendingRequests[key].timestamp > CLEANUP_THRESHOLD) {
      delete pendingRequests[key];
    }
  });
}, 30000);

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Token should already include 'Bearer ' prefix from authService
      config.headers.Authorization = token;
    }

    // Skip deduplication for certain types of requests
    if (
      config.headers['Content-Type'] === 'multipart/form-data' ||
      config.method?.toLowerCase() === 'post' ||  // Allow POST requests (creates/updates)
      config.url?.includes('upload') ||            // Allow upload endpoints
      config.url?.includes('execute') ||           // Allow execute endpoints
      config.url?.includes('run') ||               // Allow run endpoints
      config._skipDeduplication === true          // Allow manual override
    ) {
      return config;
    }

    // Create a unique key for this request (excluding timestamp-based data)
    const requestKey = `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;
    
    // Add a property to track this request
    config._requestKey = requestKey;

    // Check if we have a recent identical request (within 1 second)
    const now = Date.now();
    const pendingRequest = pendingRequests[requestKey];
    
    if (pendingRequest && (now - pendingRequest.timestamp < 1000)) {
      console.warn(`Preventing rapid duplicate request: ${config.url}`);
      
      // Cancel the request - axios will handle this properly
      const source = axios.CancelToken.source();
      config.cancelToken = source.token;
      source.cancel('Duplicate request canceled - too rapid');
    } else {
      // Mark this request as pending with timestamp
      pendingRequests[requestKey] = {
        timestamp: now,
        pending: true
      };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Clean up the pending request reference
    if (response.config._requestKey) {
      delete pendingRequests[response.config._requestKey];
    }
    
    // Return the data from the response
    return response.data;
  },
  (error) => {
    // Handle cancel errors more gracefully
    if (axios.isCancel(error)) {
      // Clean up pending request for canceled requests too
      if (error.config?._requestKey) {
        delete pendingRequests[error.config._requestKey];
      }
      
      // Don't log canceled errors as they're expected
      console.debug('Request canceled (deduplication):', error.message);
      return Promise.reject(error);
    }
    
    // For actual errors, clean up pending request if applicable
    if (error.config?._requestKey) {
      delete pendingRequests[error.config._requestKey];
    }

    if (error.response?.status === 401) {
      // Handle unauthorized access
      if (msalInstance) {
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
          msalInstance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin + loginPath,
          });
        }
      }
      console.log("Removing the token")
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      window.location.href = loginPath;


    } else if (error.response) {
      console.error(`API Error (${error.response.status}): ${error.config?.url}`, error.response.data);
    } else if (error.request) {
      console.error(`API No Response: ${error.config?.url}`, error.request);
    } else {
      console.error(`API Request Error: ${error.message}`);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Make a request with form data
 * @param {string} endpoint - API endpoint
 * @param {Object} formData - Form data to send
 * @param {Object} config - Additional axios config
 * @returns {Promise<any>} - Response data
 */
export const postForm = (endpoint, formData, config = {}) => {
  const formDataObj = new FormData();
  
  // Convert object to FormData
  Object.keys(formData).forEach(key => {
    formDataObj.append(key, formData[key]);
  });
  
  return axiosInstance.post(endpoint, formDataObj, {
    ...config,
    headers: {
      ...config.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const get = (endpoint, config = {}) => axiosInstance.get(endpoint, config);
export const post = (endpoint, data, config = {}) => axiosInstance.post(endpoint, data, config);
export const put = (endpoint, data, config = {}) => axiosInstance.put(endpoint, data, config);
export const patch = (endpoint, data, config = {}) => axiosInstance.patch(endpoint, data, config);
export const del = (endpoint, config = {}) => axiosInstance.delete(endpoint, config);

// Utility functions to bypass deduplication when needed
export const getWithoutDeduplication = (endpoint, config = {}) => 
  axiosInstance.get(endpoint, { ...config, _skipDeduplication: true });

export const postWithoutDeduplication = (endpoint, data, config = {}) => 
  axiosInstance.post(endpoint, data, { ...config, _skipDeduplication: true });

export const putWithoutDeduplication = (endpoint, data, config = {}) => 
  axiosInstance.put(endpoint, data, { ...config, _skipDeduplication: true });

export const delWithoutDeduplication = (endpoint, config = {}) => 
  axiosInstance.delete(endpoint, { ...config, _skipDeduplication: true });

export const postStream = async (endpoint, data, options = {}) => {
  const token = localStorage.getItem('authToken');
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  
  const config = {
    method: 'POST',
    headers: {
      'Accept': '*/*',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {}),
      ...options.headers,
    },
    body: JSON.stringify(data),
    ...options,
  };
  
  const response = await fetch(url, config);
  
  // Handle 401 Unauthorized
  if (response.status === 401) {
    if (msalInstance) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + loginPath,
        });
      }
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    window.location.href = loginPath;
    throw new Error('Unauthorized');
  }
  
  return response;
};

export const configureMsal = (instance) => {
  msalInstance = instance;
};

// Create the API object
const apiUtils = {
  get,
  post,
  put,
  patch,
  delete: del,
  postForm,
  postStream,
  getWithoutDeduplication,
  postWithoutDeduplication,
  putWithoutDeduplication,
  delWithoutDeduplication
};

export default apiUtils;
